<template>
  <!-- Desktop: full-screen sliding carousel -->
  <section class="main-project-carousel-lg desktop-only">
    <div class="project-carousel-lg-track" :style="{ transform: `translateX(-${currentIndex * 100}%)` }">
      <div
        v-for="(project, index) of data.projects"
        :key="index"
        class="project-carousel-lg-slide"
      >
        <img
          :src="getProjectImage(project)"
          :alt="project.post_title"
          class="project-carousel-lg-image"
        />
        <div class="project-carousel-lg-overlay"></div>
        <div class="project-carousel-lg-info">
          <a :href="`/project/${project.post_name}`" @click.prevent="goToProject(project)" class="project-carousel-lg-breadcrumb">
            <svg width="17" height="10" viewBox="0 0 17 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect y="7" width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect x="7" width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect x="7" y="7" width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect x="14" width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect x="14" y="7" width="3" height="3" fill="white" fill-opacity="0.5"/>
            </svg>
            <span v-html="project.post_title"></span>
          </a>
          <p v-if="project.post_excerpt" class="project-carousel-lg-excerpt" v-html="project.post_excerpt"></p>
        </div>
      </div>
    </div>

    <!-- Navigation arrows -->
    <button class="project-carousel-lg-nav project-carousel-lg-prev" @click="prev" aria-label="Previous project">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="15 18 9 12 15 6"></polyline>
      </svg>
    </button>
    <button class="project-carousel-lg-nav project-carousel-lg-next" @click="next" aria-label="Next project">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    </button>

    <!-- Dots indicator -->
    <div class="project-carousel-lg-dots">
      <button
        v-for="(project, index) of data.projects"
        :key="index"
        class="project-carousel-lg-dot"
        :class="{ active: index === currentIndex }"
        @click="goToSlide(index)"
        :aria-label="`Go to project ${index + 1}`"
      ></button>
    </div>
  </section>

  <!-- Mobile: horizontal scroll cards -->
  <section class="main-project-carousel-lg-mobile mobile-only">
    <div ref="mobileContainer" class="project-carousel-lg-mobile-container">
      <div
        v-for="(project, index) of data.projects"
        :key="index"
        class="project-carousel-lg-mobile-card"
        :data-index="index"
      >
        <img
          :src="getProjectImage(project)"
          :alt="project.post_title"
          class="project-carousel-lg-mobile-image"
        />
        <div class="project-carousel-lg-mobile-overlay"></div>
        <div class="project-carousel-lg-mobile-info">
          <a :href="`/project/${project.post_name}`" @click.prevent="goToProject(project)" class="project-carousel-lg-mobile-link">
            <svg width="17" height="10" viewBox="0 0 17 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect y="7" width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect x="7" width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect x="7" y="7" width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect x="14" width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect x="14" y="7" width="3" height="3" fill="white" fill-opacity="0.5"/>
            </svg>
            <span v-html="project.post_title"></span>
          </a>
          <p v-if="project.post_excerpt" class="project-carousel-lg-mobile-excerpt" v-html="project.post_excerpt"></p>
        </div>
      </div>
    </div>

    <!-- Mobile dots -->
    <div class="project-carousel-lg-mobile-dots">
      <button
        v-for="(project, index) of data.projects"
        :key="index"
        class="project-carousel-lg-mobile-dot"
        :class="{ active: index === mobileActiveIndex }"
        :aria-label="`Go to project ${index + 1}`"
      ></button>
    </div>
  </section>
</template>
<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps(['data'])
const currentIndex = ref(0)
const mobileActiveIndex = ref(0)
const mobileContainer = ref(null)
let touchStartX = 0
let touchEndX = 0
let observer = null
let resizeHandler = null
let mobileObserver = null

const getProjectImage = (project) => {
  if (project.featured_image?.full) return project.featured_image.full
  if (project.featured_image?.large) return project.featured_image.large
  if (project.acf?.['acf-single-portfolio-gallery']?.[0]?.url) {
    return project.acf['acf-single-portfolio-gallery'][0].url
  }
  return ''
}

const goToProject = (project) => {
  window.location.href = `/project/${project.post_name}`
}

const next = () => {
  if (currentIndex.value < props.data.projects.length - 1) {
    currentIndex.value++
  } else {
    currentIndex.value = 0
  }
}

const prev = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  } else {
    currentIndex.value = props.data.projects.length - 1
  }
}

const goToSlide = (index) => {
  currentIndex.value = index
}

const handleTouchStart = (e) => {
  touchStartX = e.changedTouches[0].screenX
}

const handleTouchEnd = (e) => {
  touchEndX = e.changedTouches[0].screenX
  const diff = touchStartX - touchEndX
  if (Math.abs(diff) > 50) {
    if (diff > 0) next()
    else prev()
  }
}

const isDesktop = () => window.matchMedia('(min-width: 960px)').matches

onMounted(() => {
  const section = document.querySelector('.main-project-carousel-lg.desktop-only')
  if (section) {
    section.addEventListener('touchstart', handleTouchStart, { passive: true })
    section.addEventListener('touchend', handleTouchEnd, { passive: true })

    const header = document.querySelector('.main-header')
    if (header) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!isDesktop()) {
              header.classList.remove('header-light')
              return
            }
            if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
              header.classList.add('header-light')
            } else {
              header.classList.remove('header-light')
            }
          })
        },
        { threshold: [0, 0.3, 0.5] }
      )
      observer.observe(section)

      resizeHandler = () => {
        if (!isDesktop()) {
          header.classList.remove('header-light')
        }
      }
      window.addEventListener('resize', resizeHandler, { passive: true })
    }
  }

  // Mobile: track visible card with IntersectionObserver
  if (mobileContainer.value) {
    const cards = mobileContainer.value.querySelectorAll('.project-carousel-lg-mobile-card')
    mobileObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const idx = parseInt(entry.target.dataset.index, 10)
            if (!isNaN(idx)) mobileActiveIndex.value = idx
          }
        })
      },
      { root: mobileContainer.value, threshold: 0.5 }
    )
    cards.forEach((card) => mobileObserver.observe(card))
  }
})

onBeforeUnmount(() => {
  const section = document.querySelector('.main-project-carousel-lg.desktop-only')
  if (section) {
    section.removeEventListener('touchstart', handleTouchStart)
    section.removeEventListener('touchend', handleTouchEnd)
  }
  if (observer) {
    observer.disconnect()
    observer = null
  }
  if (mobileObserver) {
    mobileObserver.disconnect()
    mobileObserver = null
  }
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler)
    resizeHandler = null
  }
  const header = document.querySelector('.main-header')
  if (header) header.classList.remove('header-light')
})
</script>
<style lang="scss">
/* White header when carousel is in view — mirrors child theme's body.single-projects styling */
.main-header.header-light {
  .main-header__logo svg path {
    fill: var(--white) !important;
  }

  .main-header__nav,
  .main-header__quote-btn,
  .main-header__menu-toggle {
    background-color: rgba(0, 0, 0, 0.2) !important;
    color: var(--white) !important;

    a {
      color: var(--white) !important;
    }
  }

  .main-header__menu-toggle {
    span {
      background-color: var(--white) !important;
    }
  }

  /* Search pill & dropdown */
  .search-pill {
    background-color: rgba(0, 0, 0, 0.2) !important;

    svg {
      color: var(--white) !important;
    }

    input {
      color: var(--white) !important;

      &::placeholder {
        color: rgba(255, 255, 255, 0.5) !important;
      }
    }
  }

  .search-pill__clear svg {
    color: var(--white) !important;
  }

  .search-dropdown {
    background-color: rgba(0, 0, 0, 0.3) !important;
    backdrop-filter: blur(30px) !important;

    .search-result-item {
      color: var(--white) !important;

      &:hover {
        background: rgba(255, 255, 255, 0.1) !important;
      }

      .result-sku {
        color: rgba(255, 255, 255, 0.5) !important;
      }

      .result-arrow svg,
      .result-icon svg {
        color: var(--white) !important;
      }
    }

    .search-status {
      color: rgba(255, 255, 255, 0.6) !important;
    }
  }

  /* When scrolled AND light, revert to dark header since background is solid */
  &.is-scrolled {
    .main-header__logo svg path {
      fill: var(--black) !important;
    }

    .main-header__nav,
    .main-header__quote-btn,
    .main-header__menu-toggle {
      background-color: rgba(0, 0, 0, 0.1) !important;
      color: var(--black) !important;

      a {
        color: var(--black) !important;
      }
    }

    .main-header__menu-toggle {
      span {
        background-color: var(--black) !important;
      }
    }

    .search-pill {
      background-color: rgba(0, 0, 0, 0.1) !important;

      svg {
        color: var(--black) !important;
      }

      input {
        color: var(--black) !important;

        &::placeholder {
          color: rgba(0, 0, 0, 0.4) !important;
        }
      }
    }

    .search-pill__clear svg {
      color: var(--black) !important;
    }

    .search-dropdown {
      background-color: rgba(0, 0, 0, 0.1) !important;

      .search-result-item {
        color: var(--black) !important;

        &:hover {
          background: rgba(0, 0, 0, 0.06) !important;
        }

        .result-sku {
          color: rgba(0, 0, 0, 0.5) !important;
        }

        .result-arrow svg,
        .result-icon svg {
          color: var(--black) !important;
        }
      }

      .search-status {
        color: rgba(0, 0, 0, 0.5) !important;
      }
    }
  }
}
</style>
<style lang="scss" scoped>
/* ===================== */
/* DESKTOP: Full-screen  */
/* ===================== */
.desktop-only {
  @apply hidden;
  @media (min-width: 960px) {
    @apply block;
  }
}

.main-project-carousel-lg {
  @apply relative w-full overflow-hidden;
  height: 100dvh;
  margin-top: -100px;
  border-radius: 0 0 24px 24px;
  -webkit-mask-image: radial-gradient(white, black);
  mask-image: radial-gradient(white, black);

  .project-carousel-lg-track {
    @apply flex w-full h-full;
    transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);

    .project-carousel-lg-slide {
      @apply relative flex-shrink-0;
      width: 100%;
      height: 100%;

      .project-carousel-lg-image {
        @apply w-full h-full object-cover;
      }

      .project-carousel-lg-overlay {
        @apply absolute inset-0 pointer-events-none;
        background: rgba(0, 0, 0, 0.3);
      }

      .project-carousel-lg-info {
        @apply absolute left-0 z-2 flex items-center gap-6;
        bottom: 80px;
        left: 60px;
        right: 60px;

        .project-carousel-lg-breadcrumb {
          @apply flex items-center gap-3 no-underline flex-shrink-0;
          background: rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border-radius: 50px;
          padding: 12px 24px;
          color: rgba(255, 255, 255, 0.85);
          font-size: 16px;
          font-weight: 400;
          letter-spacing: 0.02em;
          transition: background 0.3s ease;

          &:hover {
            background: rgba(0, 0, 0, 0.4);
            color: #fff;
          }

          svg {
            flex-shrink: 0;
          }
        }

        .project-carousel-lg-excerpt {
          @apply text-white/70 font-FHOscar font-light m-0;
          font-size: 16px;
          line-height: 1.5;
          max-width: 500px;
        }
      }
    }
  }

  .project-carousel-lg-nav {
    @apply absolute top-1/2 -translate-y-1/2 z-10 flex items-center justify-center
    text-white cursor-pointer border-0 transition-all duration-300;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(4px);

    &:hover {
      background: rgba(0, 0, 0, 0.5);
    }

    svg {
      width: 28px;
      height: 28px;
    }
  }

  .project-carousel-lg-prev {
    left: 40px;
  }

  .project-carousel-lg-next {
    right: 40px;
  }

  .project-carousel-lg-dots {
    @apply absolute left-1/2 -translate-x-1/2 z-10 flex gap-3;
    bottom: 32px;

    .project-carousel-lg-dot {
      @apply cursor-pointer border-0 rounded-full transition-all duration-300;
      width: 12px;
      height: 12px;
      background: rgba(255, 255, 255, 0.4);

      &.active {
        background: rgba(255, 255, 255, 1);
        width: 32px;
        border-radius: 6px;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.7);
      }
    }
  }
}

/* ===================== */
/* MOBILE: Scroll cards  */
/* ===================== */
.mobile-only {
  @apply block;
  @media (min-width: 960px) {
    @apply hidden;
  }
}

.main-project-carousel-lg-mobile {
  @apply relative z-1 overflow-hidden px-4;
  padding-top: 100px;
  padding-bottom: 40px;

  .project-carousel-lg-mobile-container {
    @apply flex flex-row overflow-x-auto snap-x snap-mandatory gap-4;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }

    .project-carousel-lg-mobile-card {
      @apply flex-shrink-0 overflow-hidden relative snap-center rounded-2xl;
      width: calc(100vw - 48px);
      height: 500px;

      .project-carousel-lg-mobile-image {
        @apply w-full h-full object-cover;
      }

      .project-carousel-lg-mobile-overlay {
        @apply absolute inset-0 pointer-events-none;
        background: linear-gradient(
          to top,
          rgba(0, 0, 0, 0.6) 0%,
          rgba(0, 0, 0, 0.1) 35%,
          transparent 55%
        );
      }

      .project-carousel-lg-mobile-info {
        @apply absolute bottom-0 left-0 right-0 p-5 z-2 flex flex-col gap-2;

        .project-carousel-lg-mobile-link {
          @apply flex items-center gap-2 no-underline self-start;
          background: rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border-radius: 50px;
          padding: 10px 18px;
          color: rgba(255, 255, 255, 0.85);
          font-size: 13px;
          font-weight: 400;
          letter-spacing: 0.02em;

          svg {
            flex-shrink: 0;
            width: 14px;
            height: 8px;
          }
        }

        .project-carousel-lg-mobile-excerpt {
          @apply text-white/60 font-FHOscar font-light m-0;
          font-size: 13px;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      }
    }
  }

  .project-carousel-lg-mobile-dots {
    @apply flex justify-center gap-2 mt-4;

    .project-carousel-lg-mobile-dot {
      @apply border-0 rounded-full transition-all duration-300 cursor-default;
      width: 8px;
      height: 8px;
      background: rgba(0, 0, 0, 0.15);

      &.active {
        background: rgba(0, 0, 0, 0.5);
        width: 24px;
        border-radius: 4px;
      }
    }
  }
}
</style>
